// Firebase bootstrap for the Point of Sale build.
//
// Ported from the court-booking system's firebase.js and pointed at the SAME
// project: the till reads and writes posProducts / posCategories / posOrders /
// posAuditLog there, and reads `config` to know whether it is open and which
// club it belongs to.
//
// Trimmed to the two services the POS uses — Realtime Database and Auth.
// Storage and Analytics were initialised in the original for the booking
// screens' uploads and page tracking; nothing here imports them, and
// initialising a service no one uses only adds a way for boot to fail.
import { initializeApp } from 'firebase/app';
import { getAuth, initializeAuth, inMemoryPersistence } from 'firebase/auth';
import { getDatabase } from 'firebase/database';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
};

/**
 * Whether .env actually carries a project, rather than a blank or
 * still-placeholder one.
 *
 * @returns {boolean}
 */
const isFirebaseConfigured = () => Boolean(
  firebaseConfig.apiKey
  && !firebaseConfig.apiKey.includes('YOUR_')
  && firebaseConfig.projectId
  && !firebaseConfig.projectId.includes('YOUR_'),
);

let app = null;
let db = null;
let auth = null;
let firebaseError = null;

// A misconfigured checkout must not take the whole bundle down at import
// time. Everything downstream already copes with a null db/auth — the config
// subscription reports "Database not configured" and AuthGuard falls through
// to its connection-error screen — which is a readable failure rather than a
// blank page and a stack trace in the console.
try {
  if (!isFirebaseConfigured()) {
    throw new Error('Firebase not configured. Copy .env.example to .env and fill in your project details.');
  }
  app = initializeApp(firebaseConfig);
  db = getDatabase(app);
  auth = getAuth(app);
} catch (e) {
  firebaseError = e.message;
  console.error('Firebase Error:', e.message);
}

// A SECOND Firebase app, used only to check a password.
//
// Firebase Auth keeps one signed-in user per app instance, and the primary
// instance above is persisted to browser storage — shared by every tab on this
// origin. So signing a member of staff in on it would replace whoever else is
// signed in: an administrator in another tab, or another client's till.
//
// This instance exists so a till can prove someone knows their password
// WITHOUT that side effect. It is created on first use, signs in, and signs
// straight back out; `inMemoryPersistence` means it never writes to storage
// and never leaks into another tab. The till then holds the verified address
// in its own React state, which is per tab by construction — which is exactly
// what "each client and the admin screen have separate sessions" means.
//
// The till's database access continues on the primary instance. That is
// sound because the rules gate a client's trading data on `auth != null`
// rather than on WHICH account: who may stand at a given till is decided by
// that client's staff list, which the app checks, not by the token.
let credentialCheckAuth = null;

/**
 * The auth instance used only for verifying credentials.
 *
 * Built lazily: a browser that never signs in at a till never creates it.
 *
 * @returns {?import('firebase/auth').Auth} null when Firebase is not configured.
 */
export const getCredentialCheckAuth = () => {
  if (!app) return null;
  if (!credentialCheckAuth) {
    const secondary = initializeApp(firebaseConfig, 'pos-credential-check');
    // initializeAuth rather than getAuth: it sets persistence before any
    // session can be restored, so this instance never touches storage at all.
    credentialCheckAuth = initializeAuth(secondary, { persistence: inMemoryPersistence });
  }
  return credentialCheckAuth;
};

export { db, auth, firebaseError };

// Optional: if the raw app instance is ever needed elsewhere.
export default app;
